import {createAudioFeedback} from './AudioFeedback';

let audioContext;
let feedback;
let instrumentPromise;

function reportState(state) {
    window.dispatchEvent(new CustomEvent('guitarcharts:audio-state', {detail: state}));
}

function getInstrument() {
    if (!instrumentPromise) {
        instrumentPromise = loadInstrument().catch(error => {
            instrumentPromise = undefined;
            throw error;
        });
    }
    return instrumentPromise;
}

async function loadInstrument() {
    const {Soundfont} = await import(/* webpackChunkName: "guitar-audio" */ 'smplr');
    const guitar = Soundfont(audioContext, {
        // Pin the existing MP3 samples: smplr otherwise prefers Ogg where supported.
        instrumentUrl: 'https://gleitz.github.io/midi-js-soundfonts/MusyngKite/acoustic_guitar_steel-mp3.js',
        // Use smplr's default volume, velocity and guitar gain.
        loadLoopData: false,
        storage: {
            async fetch(url) {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Guitar download failed: ${response.status}`);
                return response;
            },
        },
    });
    try {
        await guitar.ready;
        // smplr skips failed decodes and can substitute a neighbouring pitch.
        // Keep all 88 original samples, or allow the next click to retry.
        if (guitar.loadProgress.loaded !== 88) throw new Error('Incomplete guitar samples');
        return guitar;
    } catch (error) {
        guitar.dispose();
        throw error;
    }
}

async function getReadyInstrument() {
    if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        feedback = createAudioFeedback(audioContext);
    }
    // Start resume inside the user gesture, before waiting for any network IO.
    // Safari can also enter "interrupted" after an audio interruption.
    const resumed = audioContext.state === 'running' ? Promise.resolve() : audioContext.resume();
    if (!instrumentPromise) reportState('loading');
    const [guitar] = await Promise.all([getInstrument(), resumed]);
    reportState('ready');
    return guitar;
}

// The chord game plays its first answer after a timer. Unlock audio while its
// Play button still has user activation, without playing a note early.
export async function prepareAudio() {
    try {
        await getReadyInstrument();
        return true;
    } catch (error) {
        reportState('error');
        console.error('Unable to prepare guitar audio', error);
        return false;
    }
}

// Shared instrument/output for the separately loaded tuning-page loop player.
export async function getReadyAudio() {
    const instrument = await getReadyInstrument();
    return {instrument, context: audioContext, feedback};
}

async function play(midiNotes, spacing, onStart) {
    if (midiNotes.length === 0) return true;
    try {
        const guitar = await getReadyInstrument();
        const time = audioContext.currentTime;
        midiNotes.forEach((note, index) => guitar.start({
            note, time: time + spacing * index,
        }));
        // Queue visuals only after sample readiness and successful scheduling.
        // smplr's onStart runs during lookahead, not at speaker output.
        // Keep the same note times for audio and the output-clock visual queue.
        if (onStart) midiNotes.forEach((note, index) => {
            feedback(time + spacing * index, () => onStart(note, index));
        });
        return true;
    } catch (error) {
        reportState('error');
        // Inline chart/game handlers do not await playback; never leave a rejected
        // promise unhandled. A later user gesture can retry loading or resuming.
        console.error('Unable to play guitar audio', error);
        return false;
    }
}

export function playMidiNotes(midiNotes, onStart) {
    return play(midiNotes, 0.4, onStart);
}

export function playMidiChord(midiNotes, onStart) {
    return play(midiNotes, 0, onStart);
}
